import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import classnames from 'classnames'

import type { PortableTextComponentProps } from '~/components/atoms/BlockContent/BlockContent'
import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import CollapsibleReadMore from '~/components/atoms/CollapsibleReadMore/CollapsibleReadMore'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import { Icon } from '~/components/atoms/Icon/Icon'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import type { VideoEmbedProps } from '~/components/atoms/VideoEmbed/VideoEmbed'
import VideoEmbed from '~/components/atoms/VideoEmbed/VideoEmbed'
import type { ComponentTheme } from '~/components/themes'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import useSearchAnalyst from '~/services/elastic/analyst'
import { toURL } from '~/utils/urlResolver'

import styles from './NumberedSlider.module.css'
import EsIndexTypes = indexes.EsIndexTypes
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'

export interface NumberedSliderSlideProps {
  title?: string
  image?: ImageProps
  text: PortableTextComponentProps['blocks']
  videoSrc?: VideoEmbedProps['videoSrc']
  videoType?: VideoEmbedProps['videoType']
  artwork?: {
    url: any
    title: string
  }
}

export interface NumberedSliderProps {
  title: string
  linkText?: string
  relatedArticle?: {
    slug: string
    _type: string
    articleTaxonomy?: string
    parent: {
      _type: string
      slug: string
    }
  }
  slides: NumberedSliderSlideProps[]
  seeMoreText?: string
  seeMoreButtonText: string
  totalItems: number
  theme?: ComponentTheme
  elementId?: string
  testID?: string
  searchInsightEvent?: TSearchAnalystEventProps
}

export default function NumberedSlider({
  title,
  linkText,
  relatedArticle,
  slides,
  theme,
  elementId,
  totalItems,
  seeMoreText,
  seeMoreButtonText,
  searchInsightEvent,
}: NumberedSliderProps) {
  const tCommon = useTranslations('common')
  const tMagazine = useTranslations('magazineImage')
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScroll, setCanScroll] = useState({
    left: false,
    right: false,
  })

  useEffect(() => {
    if (!scrollRef.current) return
    // Get width of the slide
    const slideWidth = scrollRef.current.children[0]
      ? scrollRef.current.children[0].clientWidth
      : 0

    if (scrollRef.current) {
      scrollRef.current?.scrollTo({
        behavior: 'smooth',
        left: slideWidth * currentSlide,
      })
    }

    const adjustScrollPos = () => {
      if (scrollRef.current) {
        scrollRef.current?.scrollTo({
          left: slideWidth * currentSlide,
        })
      }
    }

    // Resize listener to adjust scroll when they adjust size of browser.
    window.addEventListener('resize', adjustScrollPos)

    // Cleanup the listeners
    return function cleanup() {
      window.removeEventListener('resize', adjustScrollPos)
    }
  }, [currentSlide])

  function checkForScrollPosition() {
    if (!scrollRef.current) return
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current

    setCanScroll({
      left: scrollLeft > 0,
      right: scrollLeft < scrollWidth - clientWidth,
    })
  }

  useEffect(() => {
    checkForScrollPosition()
    scrollRef.current?.addEventListener('scroll', checkForScrollPosition, {
      passive: true,
    })

    return () =>
      scrollRef.current?.removeEventListener('scroll', checkForScrollPosition)
  }, [])

  let relatedArticleLink: string | undefined = undefined

  if (relatedArticle) {
    if (relatedArticle._type !== null) {
      const obj = {
        slug: relatedArticle.slug,
        _type: relatedArticle._type,
        articleTaxonomy: relatedArticle.articleTaxonomy,
      } as any
      if (
        relatedArticle.parent._type !== null &&
        relatedArticle.parent.slug !== null
      ) {
        obj['parent'] = {
          _type: relatedArticle.parent._type,
          slug: relatedArticle.parent.slug,
        }
      }

      relatedArticleLink = toURL(obj)
    }
  }

  // We can also trim down the number of slides to 4 as we are linking off to an article
  const localSlides = relatedArticle ? slides.slice(0, 4) : slides?.slice()

  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const onRelatedArticleClick = useCallback(async () => {
    if (searchInsightEvent && relatedArticle?.slug) {
      sendSearchInsightsEvent(searchInsightEvent.method, {
        eventName: searchInsightEvent.name,
        indexType: EsIndexTypes.ARTICLES,
        filters: [`slug:${relatedArticle.slug}`],
      })
    }
    await multiPostHogEventPusher([
      {
        eventType: searchInsightEvent?.name || `All record prices clicked`,
      },
    ])
  }, [relatedArticle?.slug, searchInsightEvent])

  if (slides === null || !slides) return null

  return (
    <div
      className={classnames(styles.mainwrap, theme && styles[theme])}
      id={elementId}
    >
      <Fixture verticalPadding={true} theme={theme}>
        <Container gutters={gutterTypes.never}>
          <Container gutters={gutterTypes.always}>
            <div className={classnames(styles.introText, 'prose')}>
              <Text Tag="h2" size={TextSizes.primaryTitleLrg}>
                {title}
              </Text>
            </div>
            {totalItems > 3 && linkText && relatedArticle && (
              <div className={styles.articleLink}>
                <Button
                  variant={ButtonVariants.secondaryDark}
                  iconLeft={true}
                  inline={true}
                  link={relatedArticleLink}
                  linkOnclick={onRelatedArticleClick}
                >
                  {linkText}
                </Button>
              </div>
            )}
          </Container>

          <div
            className={classnames(styles.sliderWrap, theme && styles[theme])}
          >
            <div className={styles.slider} ref={scrollRef}>
              {localSlides.map((slide, i) => {
                // Do we have more than 4 slides in total? If we do show the preview slide with a link out to the related article
                const showPreview = i === 3 && totalItems > 4 && relatedArticle

                return (
                  <div key={`numberedSlider-${i}`} className={styles.slide}>
                    <div
                      className={classnames(
                        styles.slideImage,
                        slide.videoSrc && styles.slideImageVideo
                      )}
                    >
                      <div
                        className={classnames(
                          styles.slideImageMedia,
                          slide.videoSrc && styles.slideImageMediaVideo
                        )}
                      >
                        {slide.videoSrc && slide.videoType && (
                          <VideoEmbed
                            videoSrc={slide.videoSrc}
                            videoType={slide.videoType}
                            className={styles.slideVideoWrapper}
                          />
                        )}
                        {slide.image && !slide.videoSrc && (
                          <Image
                            asset={slide.image.asset}
                            sizes="(max-width: 799px) 100vw, 400px"
                            alt={slide.image.alt}
                          />
                        )}
                      </div>
                      {slide.image && slide.image.credit && (
                        <div className={styles.slideImageCredit}>
                          <Text Tag="p" size={TextSizes.tertiaryTitleSml}>
                            {slide.image.credit.text}
                          </Text>
                        </div>
                      )}
                      {/* {slide.artwork && ( */}
                      {/*   <Button */}
                      {/*     variant={ButtonVariants.textLinkSmall} */}
                      {/*     link={toURL(slide.artwork.url)} */}
                      {/*   > */}
                      {/*     {tMagazine('buyOrSellArtwork')} {slide.artwork.title} */}
                      {/*   </Button> */}
                      {/* )} */}
                    </div>
                    <div className={styles.slideText}>
                      <div className={styles.slideTextInner}>
                        {slide.title && (
                          <Text
                            size={TextSizes.primaryTitleLrgPlus}
                            className={styles.slideTitle}
                            Tag="h3"
                          >
                            {showPreview ? (
                              <span>
                                {i + 1} - {totalItems}.
                              </span>
                            ) : (
                              <>
                                <span>{i + 1}.</span> <span>{slide.title}</span>
                              </>
                            )}
                          </Text>
                        )}
                        <div className="prose">
                          {showPreview && (
                            <>
                              {seeMoreText && (
                                <Text size={TextSizes.bodySml}>
                                  {seeMoreText}
                                </Text>
                              )}
                              <div className={styles.articleButton}>
                                <Button
                                  variant={ButtonVariants.secondaryDark}
                                  iconLeft
                                  link={relatedArticleLink}
                                >
                                  {seeMoreButtonText}
                                </Button>
                              </div>
                            </>
                          )}

                          {slide.text && !showPreview && (
                            <CollapsibleReadMore
                              maxHeight={120}
                              theme={theme}
                              readMoreText="Expand"
                              onlyMobile={true}
                            >
                              <BlockContent
                                value={slide.text}
                                size={TextSizes.bodySml}
                              />
                            </CollapsibleReadMore>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {currentSlide > 0 && canScroll.left && (
              <button
                onClick={() => setCurrentSlide(currentSlide - 1)}
                className={styles.prevBtn}
              >
                <span>{tCommon('previous')}</span>
                <Icon type="Chevron" />
              </button>
            )}

            {currentSlide + 1 !== slides.length && canScroll.right && (
              <button
                onClick={() => setCurrentSlide(currentSlide + 1)}
                className={styles.nextBtn}
              >
                <span>{tCommon('next')}</span>
                <Icon type="Chevron" />
              </button>
            )}
          </div>
        </Container>
      </Fixture>
    </div>
  )
}
